import { useCallback, useContext } from 'react'
import cn from 'classnames'
import { useDropzone, type FileError, type FileRejection } from 'react-dropzone'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUpload, faTimes } from '@fortawesome/pro-light-svg-icons'
import { faCheckCircle, faTimesCircle } from '@fortawesome/pro-solid-svg-icons'
import { Line } from 'rc-progress'
import filesize from 'filesize'

import Button from './Button'
import BrandingContext from '../context/BrandingContext'

const unsupportedMimeTypes = [
  'application/zip',
  'application/x-zip-compressed',
  'multipart/x-zip',
  'application/vnd.rar',
  'application/x-rar-compressed',
]

export type TFileDropzoneInputFile = File & {
  rejectReason?: string
}

export type TFileDropzoneFile = {
  id?: string | null
  name: string
  size: number
  fileBlob?: File
  isUploading?: boolean
  uploadProgress?: number | null
  isUploaded?: boolean
  isAccepted: boolean
  isRejected: boolean
  rejectReason?: string | null
  uploadErr?: unknown
  uploadUrl?: string | null
  isPreparing?: boolean | null
}

type Props = {
  disabled?: boolean
  files: TFileDropzoneFile[]
  onAccepted: (files: File[]) => void
  onRejected: (fileRejections: FileRejection[]) => void
  onRemoveFile: (index: number) => void
  multiple?: boolean
}

const FileDropzone = (props: Props) => {
  const {
    disabled = false,
    files,
    onAccepted,
    onRejected,
    onRemoveFile,
    multiple = true,
  } = props
  const [branding] = useContext(BrandingContext)
  const { primaryColorPalette } = branding

  const handleNewAccepted = useCallback(onAccepted, [onAccepted])
  const handleNewRejected = useCallback(onRejected, [onRejected])
  const handleRemoveFile = useCallback(onRemoveFile, [onRemoveFile])

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    disabled,
    onDropAccepted: handleNewAccepted,
    onDropRejected: handleNewRejected,
    multiple,
    maxSize: 1024 * 1024 * 25,
    noClick: true,
    noKeyboard: true,
    validator: (file): FileError | null => {
      if (unsupportedMimeTypes.includes(file.type)) {
        return {
          code: 'zip-folder',
          message: 'ZIP/RAR folders are unsupported',
        }
      }

      return null
    },
  })

  const renderFileStatus = (file: TFileDropzoneFile) => {
    if (file.isPreparing) {
      return <p className="text-xs text-gray-600">Preparing...</p>
    }

    if (file.isUploaded) {
      return <p className="text-xs text-gray-600">Uploaded</p>
    }

    if (file.uploadErr) {
      return <p className="text-xs text-red-600">Error uploading file</p>
    }

    if (file.isRejected) {
      return <p className="text-xs text-red-600">{file.rejectReason}</p>
    }

    if (file.uploadProgress !== null && file.uploadProgress !== undefined) {
      return (
        <div className="flex items-center">
          <p className="text-xs mr-1.5 text-gray-600">
            Uploading ({file.uploadProgress}%)
          </p>
          <Line
            className="block h-2 w-25"
            percent={file.uploadProgress}
            strokeWidth={6}
            strokeColor="#2563eb"
            trailColor="#2563eb"
            trailWidth={6}
          />
        </div>
      )
    }

    return <p className="text-xs text-gray-600">Ready to upload</p>
  }

  const renderFileIcon = (file: TFileDropzoneFile) => {
    if (file.isAccepted) {
      if (file.isUploaded) {
        return (
          <FontAwesomeIcon
            icon={faCheckCircle}
            className="text-green-600 mt-1"
          />
        )
      }

      return (
        <FontAwesomeIcon icon={faCheckCircle} className="text-gray-400 mt-1" />
      )
    }

    return (
      <FontAwesomeIcon icon={faTimesCircle} className="text-red-600 mt-1" />
    )
  }

  const uploadCn = cn(
    'border-2 border-dashed border-gray-300 rounded-md py-12 px-4',
    {
      'opacity-60': disabled,
      'bg-gray-200': isDragActive,
    },
  )

  return (
    <>
      <div {...getRootProps({ className: uploadCn })}>
        <input {...getInputProps()} />
        <div className="flex items-center justify-center text-gray-500">
          <FontAwesomeIcon icon={faUpload} size="3x" />
        </div>
        <div className="flex flex-col items-center">
          <h2 className="mt-4 text-base font-medium text-center">
            {isDragActive
              ? 'Drop your files here to upload'
              : 'Drag and drop anywhere to upload'}
          </h2>
          <p className="my-2 text-sm uppercase text-gray-600 grow-0">or</p>
          <Button size="sm" color="primary" onClick={open} disabled={disabled}>
            {isDragActive ? 'Dropping files...' : 'Browse your files'}
          </Button>
        </div>
      </div>
      {files.length > 0 && (
        <div className="mt-5 mb-3">
          <h5 className="text-sm font-medium mb-3">
            File{files.length > 1 ? 's' : ''} selected
          </h5>
          <div className="space-y-2">
            {files.map((file, index) => (
              <div
                key={`${file.name}-${index}`}
                className="flex items-center justify-between py-3 rounded-md px-4 border border-gray-200 shadow-xs bg-white"
              >
                <div className="flex min-w-0 items-start space-x-2">
                  {renderFileIcon(file)}
                  <div className="min-w-0">
                    <p className="font-medium text-sm mb-0.5 truncate">
                      {file.name} ({filesize(file.size)})
                    </p>
                    {renderFileStatus(file)}
                  </div>
                </div>
                {!file.isUploaded && (
                  <button
                    className={`rounded-md h-8 w-8 flex items-center justify-center hover:bg-gray-100 focus:outline-hidden focus:ring-2 focus:ring-${primaryColorPalette}-600`}
                    disabled={disabled}
                    onClick={() => handleRemoveFile(index)}
                  >
                    <FontAwesomeIcon
                      size="1x"
                      className="text-gray-500"
                      icon={faTimes}
                    />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  )
}

export default FileDropzone
